import React, { FunctionComponent, ReactNode, useMemo } from 'react'
import { Button, Divider, Heading, Icon, Link, Text, useDate, useIsBreakpointLargeAndUp, useModalDeepLink } from '@afterpaytouch/core'
import { Agreement, AgreementStatus } from '@afterpaytouch/portal-api/consumer/billingAgreements/types'
import { useTranslation } from 'next-i18next'
import { useRouter } from 'next/router'
import { PreferredPaymentDisclaimerRow, PaymentMethodRow } from '../PaymentMethodRow'
import { createModalHook, useFlag, useSearchParam } from '../../hooks'
import { TrackingEvent } from '../../model/amplitude'
import { useAmplitudeWithEnduringEventProperties } from '../../integrations/amplitude'
import { ChangePaymentMethod } from '../ChangePaymentMethod/'
import { useUpdatePaymentBusinessLogic } from '../AgreementPaymentMethod/hooks'
import { useConsumerCountry, useFindBillingAgreementByIdDataSelector } from '../../state'
import { mapContryCodeToCurrency } from '../Money/utils'
import { maskCreditCardNumber } from '../../utils/card'
import { Route } from '../../config/router'
import { stringToBoolean } from '@afterpay/utils'
import { CancelAgreementModal } from './CancelAgreementModal'
import { useCancelAgreementLogic } from './hooks'
import { SupportedLocale } from '@afterpay/types'

const I18N_NAMESPACE = ['common', 'recurringPayments', 'orders']

export const useChangePaymentMethodModal = createModalHook()
export const useCancelAgreementModal = createModalHook()
interface BillingAgreementDetailProps {
  agreementId: number
  onMenuToggle?: () => void
  menuActive?: boolean
}

interface DetailLineProps {
  label: ReactNode
  value: ReactNode
  testNameSpace?: string
  forStatus?: boolean
  activeStatus?: boolean
}
export const DetailLine: FunctionComponent<DetailLineProps> = ({ label, value, testNameSpace = '', forStatus = false, activeStatus }) => (
  <div
    className='relative'
    {...(testNameSpace.length > 0 && {
      'data-testid': `${testNameSpace}-summary-row`,
    })}
  >
    <div className='relative mt-4 flex items-center'>
      <div className='mr-4 flex-1'>
        <Text renderAs='span' bold size='M'>
          {label}
        </Text>
      </div>
      <div
        className='flex'
        {...(testNameSpace.length > 0 && {
          'data-testid': `${testNameSpace}-summary-row-value`,
        })}
      >
        {forStatus ? (
          <Text renderAs='span' size='M' bold color={activeStatus ? 'Moss Gem' : 'Fire'}>
            {value}
          </Text>
        ) : (
          <Text renderAs='span' size='M'>
            {value}
          </Text>
        )}
      </div>
    </div>
  </div>
)

export const MerchantLogo: FunctionComponent<{ logoUrl: string; altName: string }> = ({ logoUrl, altName }) => {
  return logoUrl ? (
    <div className='w-[48px]'>
      <img src={logoUrl} alt={altName} className='w-full object-contain'></img>
    </div>
  ) : (
    <Icon.Merchant title={altName} />
  )
}

export const BillingAgreementDetail: FunctionComponent<BillingAgreementDetailProps> = ({ agreementId, onMenuToggle, menuActive }) => {
  // @ts-ignore: OPERATION BLEED STOPPER
  const isBpLargeUp: boolean = useIsBreakpointLargeAndUp()
  const { t } = useTranslation(I18N_NAMESPACE)
  const { logEvent } = useAmplitudeWithEnduringEventProperties()
  const router = useRouter()
  const locale = router?.locale as SupportedLocale
  const { formatUnixDate } = useDate({
    locale,
    day: '2-digit',
    year: 'numeric',
    weekday: 'short',
  })
  const agreement: Agreement = useFindBillingAgreementByIdDataSelector(agreementId)
  // whether allow to change payment method or cancel the agreement
  const paymentMethodChangeable = agreement?.status === AgreementStatus.ACTIVE || agreement?.status === AgreementStatus.EXPIRED
  const agreementCancellable = agreement?.status !== AgreementStatus.CANCELLED

  const enableAchBankAccount = useFlag('billing-agreement-ach-payment-method-enabled')

  const agreementStartDate: string = useMemo(() => {
    return formatUnixDate(agreement?.createdAt)
  }, [agreement?.createdAt, formatUnixDate])

  const [isCancelAgreementModalOpen, { openModal: openCancelAgreementModal, closeModal: closeCancelAgreementModal }] = useCancelAgreementModal()

  const changePaymentMethodModalName = 'change-payment-method'
  // @ts-ignore: OPERATION BLEED STOPPER
  const isChangePaymentMethodModalOpenParam = stringToBoolean(useSearchParam(`modal-${changePaymentMethodModalName}`))
  const [isChangePaymentMethodModalOpen, { openModal: openChangePaymentMethodModal, closeModal: closeChangePaymentMethodModal }] =
    useChangePaymentMethodModal(isChangePaymentMethodModalOpenParam)
  useModalDeepLink(isChangePaymentMethodModalOpen, changePaymentMethodModalName)

  const countryCode = useConsumerCountry()
  // @ts-ignore: OPERATION BLEED STOPPER
  const currencyCode = mapContryCodeToCurrency(countryCode)

  const changePaymentMethodButton = (
    <Button.Ghost
      onClick={() => {
        openChangePaymentMethodModal()
        logEvent(TrackingEvent.CLICKED_CHANGE_BILLING_AGREEMENT_PAYMENT_METHOD)
      }}
      size={isBpLargeUp ? 'Regular' : 'Small'}
      testNameSpace='change-payment-method'
    >
      {t('orders:orderPaymentMethod:changeCTA')}
    </Button.Ghost>
  )

  return (
    <div>
      {!isBpLargeUp && !menuActive && (
        // @ts-ignore: OPERATION BLEED STOPPER
        <div className='mb-4 flex cursor-pointer flex-row items-center gap-x-2' onClick={() => onMenuToggle()} data-testid='agreement-menu-back-btn'>
          <Icon.CaretLeft title='Back' size='ExtraSmall' color='Black' role='img' />
          <Text size='S' bold testNameSpace='agreement-menu-back-btn'>
            {t('common:back')}
          </Text>
        </div>
      )}
      {(isBpLargeUp || !menuActive) && (
        <div>
          <div className='flex flex-row items-center gap-x-4'>
            {/* @ts-ignore: OPERATION BLEED STOPPER */}
            <MerchantLogo logoUrl={agreement?.merchant?.merchantLogoUrl} altName={agreement?.merchant?.name}></MerchantLogo>
            <Heading size={'XL'} testNameSpace='agreement-detail'>
              {agreement?.merchant?.name}
            </Heading>
          </div>
          <div className='mt-8'>
            {agreement?.status === AgreementStatus.EXPIRED && (
              <div className='mb-8'>
                <Text renderAs='span' size='M' color='Fire' testNameSpace='agreement-status-notes'>
                  {t('recurringPayments:billingAgreementDetail:expiredNote', { merchantName: agreement?.merchant?.name })}
                </Text>
              </div>
            )}
            <Heading size={'L'} testNameSpace='agreement-summary'>
              {t('recurringPayments:billingAgreementDetail:summary')}
            </Heading>
            <DetailLine
              label={t('recurringPayments:billingAgreementDetail:status')}
              value={t('recurringPayments:agreementStatus:' + agreement?.status)}
              testNameSpace='agreement-status'
              forStatus={true}
              activeStatus={agreement?.status === AgreementStatus.ACTIVE}
            ></DetailLine>
            <Divider kind='Hairline' />
            <DetailLine
              label={t('recurringPayments:billingAgreementDetail:startDate')}
              value={agreementStartDate}
              testNameSpace='agreement-start-date'
            ></DetailLine>
            <Divider kind='Hairline' />
          </div>

          <div className='mt-8'>
            <Heading size={'L'} testNameSpace='agreement-merchant-info'>
              {t('recurringPayments:billingAgreementDetail:merchantInfo')}
            </Heading>
            <DetailLine
              label={t('recurringPayments:billingAgreementDetail:website')}
              value={
                <a href={agreement?.merchant?.contactUrl} target='_blank' className='text-inherit underline' rel='noreferrer'>
                  {agreement?.merchant?.contactUrl}
                </a>
              }
              testNameSpace='agreement-merchant-website'
            ></DetailLine>
            <Divider kind='Hairline' />
            <DetailLine
              label={t('recurringPayments:billingAgreementDetail:email')}
              value={
                <a href={`mailto:${agreement?.merchant?.contactEmail}`} className='text-inherit underline'>
                  {agreement?.merchant?.contactEmail}
                </a>
              }
              testNameSpace='agreement-merchant-email'
            ></DetailLine>
            <Divider kind='Hairline' />
            <DetailLine
              label={t('recurringPayments:billingAgreementDetail:contactDetails')}
              value={agreement?.merchant?.contactNumber}
              testNameSpace='agreement-merchant-contact'
            ></DetailLine>
            <Divider kind='Hairline' />
          </div>

          <div className='mt-8'>
            <Heading size={'L'} testNameSpace='agreement-paymentMethod'>
              {t('recurringPayments:billingAgreementDetail:paymentMethod')}
            </Heading>
            <div className='mt-4'>
              {agreement?.usingPreferredPaymentMethod === true || agreement?.paymentMethod == null ? (
                <PreferredPaymentDisclaimerRow
                  testNameSpace={'preferred'}
                  rightAdornment={paymentMethodChangeable && changePaymentMethodButton}
                  merchantName={agreement?.merchant?.name}
                />
              ) : (
                <PaymentMethodRow
                  number={maskCreditCardNumber(agreement?.paymentMethod?.maskedPan)}
                  expiryDate={agreement?.paymentMethod?.expiryDate}
                  cardType={agreement?.paymentMethod?.cardBrand}
                  isPreferred={false}
                  type={agreement?.paymentMethod?.type}
                  testNameSpace='agreement'
                  rightAdornment={paymentMethodChangeable && changePaymentMethodButton}
                />
              )}
            </div>
            <ChangePaymentMethod
              show={isChangePaymentMethodModalOpen}
              onBackdropClick={closeChangePaymentMethodModal}
              onEscapeKeyDown={closeChangePaymentMethodModal}
              onClose={closeChangePaymentMethodModal}
              displayStorePaymentToken={false}
              agreementId={agreement?.id}
              paymentMethod={agreement?.paymentMethod}
              useUpdatePaymentBusinessLogic={useUpdatePaymentBusinessLogic}
              currency={currencyCode}
              merchant={agreement?.merchant}
              enableAchBankAccount={enableAchBankAccount}
              achFlowEntryDeepLink={`${Route.RECURRING_PAYMENTS__CHANGEPAYMENTMETHOD_ADDBANKACCOUNTMODAL}`}
              agreementUsingPreferredPaymentMethod={agreement?.usingPreferredPaymentMethod}
            />
            <Divider kind='Hairline' />
          </div>
          {agreementCancellable && (
            <>
              <div className='mt-8'>
                <Button.Ghost
                  onClick={() => {
                    openCancelAgreementModal()
                    logEvent(TrackingEvent.CLICKED_CANCEL_BILLING_AGREEMENT)
                  }}
                  size={isBpLargeUp ? 'Regular' : 'Small'}
                  testNameSpace='cancel-ba'
                >
                  {t('recurringPayments:billingAgreementDetail:cancelCTA')}
                </Button.Ghost>
              </div>
              <div className='mt-1'>
                <Text renderAs='span' size='XS' color='Gray50' testNameSpace='cancel-ba-note'>
                  {t('recurringPayments:billingAgreementDetail:cancelNote', { merchantName: agreement?.merchant?.name })}
                </Text>
              </div>
              <CancelAgreementModal
                isModalOpen={isCancelAgreementModalOpen}
                closeModal={closeCancelAgreementModal}
                merchantName={agreement?.merchant?.name}
                agreementId={agreementId}
                useCancelAgreementLogic={useCancelAgreementLogic}
              ></CancelAgreementModal>
            </>
          )}
          <div className='mt-8'>
            <Link href='orders' kind={'Underline'}>
              <Text renderAs='span' size='S' bold testNameSpace='see-all-orders'>
                {t('recurringPayments:billingAgreementDetail:seeAllOrders')}
              </Text>
            </Link>
          </div>
        </div>
      )}
    </div>
  )
}
